/**
 * A learner's IELTS progress (D-105): band per skill with its CLB (Express Entry), the overall
 * band, the gap to the target band, rank, critical points and the sittings. Always an estimate.
 */

import { type LearnerAdmin } from "@agencyos/api-client";
import { Badge, type BadgeTone, StatCard } from "@agencyos/ui-web";
import { ArrowDownRight, ArrowRight, ArrowUpRight, BookText, Building2, Globe2, Headphones, MapPin, Mic, PenLine, Trophy } from "lucide-react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import "./ielts-admin.css";
import { dateText } from "../billing/shared";
import { IELTS } from "./examContext";
import { type BandProgress, bandText, IELTS_SKILLS, type IeltsSkill, ieltsTarget } from "./ieltsData";
import { useIeltsSkillName } from "./ieltsShared";

const VERDICT_TONE: Record<BandProgress["verdict"], BadgeTone> = { ready: "success", almost: "offer", not_ready: "danger" };
const ICON: Record<IeltsSkill, ReactNode> = {
  co: <Headphones size={18} strokeWidth={1.75} />,
  ce: <BookText size={18} strokeWidth={1.75} />,
  ee: <PenLine size={18} strokeWidth={1.75} />,
  eo: <Mic size={18} strokeWidth={1.75} />,
};

function Trend({ trend }: { trend: string }) {
  const { t } = useTranslation();
  const label = t(`academyAdmin.progress.trend.${trend}`, { defaultValue: t("academyAdmin.progress.trend.unknown") });
  if (trend === "up") return <ArrowUpRight size={16} aria-label={label} className="ok" />;
  if (trend === "down") return <ArrowDownRight size={16} aria-label={label} className="ko" />;
  return <ArrowRight size={16} aria-label={label} className="muted" />;
}

export function IeltsProgressDetail({ data, learner }: { data: BandProgress; learner: LearnerAdmin }) {
  const { t, i18n } = useTranslation();
  const skillName = useIeltsSkillName();
  const profile = ieltsTarget(learner.exam_profiles, IELTS);
  const target = data.target ?? profile.target;
  const series = data.evolution.map((row) => row.overall).filter((v): v is number => v !== null);
  return (
    <div className="stack ielts-progress">
      <div className="row">
        <Badge tone={VERDICT_TONE[data.verdict]}>{t(`academyAdmin.progress.verdict.${data.verdict}`)}</Badge>
        <span className="muted">{t("academyAdmin.ielts.estimate")}</span>
      </div>
      <p className="ielts-meta">
        <span>
          <MapPin size={14} strokeWidth={1.75} aria-hidden="true" /> {t("academyAdmin.ielts.progress.centerLine", { center: profile.center || learner.exam_center || t("academyAdmin.trends.noCenter") })}
        </span>
        {profile.date ? <span>{t("academyAdmin.learners.examOn", { date: dateText(profile.date, i18n.language) })}</span> : null}
        {data.daysLeft !== null ? <span>{t("academyAdmin.progress.daysLeft", { count: data.daysLeft })}</span> : null}
      </p>
      <section className="ielts-kpis" aria-label={t("academyAdmin.ielts.progress.figures")}>
        <StatCard
          icon={<Trophy size={18} strokeWidth={1.75} />}
          label={t("academyAdmin.ielts.progress.overall")}
          value={<span className="ao-num">{bandText(data.overall, i18n.language)}</span>}
          hint={t("academyAdmin.ielts.progress.target", { band: bandText(target, i18n.language) })}
          tone={data.overall !== null && target !== null && data.overall >= target ? "success" : "accent"}
          series={series.length > 1 ? series : undefined}
          seriesLabel={t("academyAdmin.ielts.progress.overallSeries")}
        />
        <StatCard
          icon={<Globe2 size={18} strokeWidth={1.75} />}
          label={t("academyAdmin.progress.rankPlatform")}
          value={data.rank.platform ? t("academyAdmin.progress.position", { position: data.rank.platform.position, total: data.rank.platform.total }) : "—"}
          hint={data.rank.platform ? t("academyAdmin.progress.top", { percent: data.rank.platform.topPercent }) : t("academyAdmin.progress.noRank")}
          tone="info"
        />
        <StatCard
          icon={<Building2 size={18} strokeWidth={1.75} />}
          label={t("academyAdmin.progress.rankAgency")}
          value={data.rank.agency ? t("academyAdmin.progress.position", { position: data.rank.agency.position, total: data.rank.agency.total }) : "—"}
          hint={data.rank.agency ? t("academyAdmin.progress.top", { percent: data.rank.agency.topPercent }) : t("academyAdmin.progress.noRank")}
          tone="neutral"
        />
      </section>
      <section className="stack" aria-labelledby="ielts-progress-skills">
        <h3 id="ielts-progress-skills" className="ao-headline">
          {t("academyAdmin.ielts.progress.bySkill", { band: bandText(target, i18n.language) })}
        </h3>
        <ul className="ielts-skill-grid">
          {data.skills.map((s) => {
            const reached = s.band !== null && target !== null && s.band >= target;
            return (
              <li key={s.skill} className="ao-surface ao-card stack ielts-skill">
                <div className="row between">
                  <span className="row">
                    <span className="ielts-skill__icon" aria-hidden="true">
                      {ICON[s.skill]}
                    </span>
                    <strong>{skillName(s.skill)}</strong>
                  </span>
                  <Trend trend={s.trend} />
                </div>
                <div className="row ielts-skill__figures">
                  <span className="ao-title-2 ao-num">{s.band === null ? "—" : bandText(s.band, i18n.language)}</span>
                  <span className="ao-footnote ao-secondary">{t("academyAdmin.ielts.progress.bandLabel")}</span>
                  {s.clb !== null ? <Badge tone={reached ? "success" : "info"}>{t("academyAdmin.ielts.progress.clb", { clb: s.clb })}</Badge> : null}
                </div>
                <div className="meter__bar" role="presentation">
                  <div className={`meter__fill${reached ? " meter__fill--ok" : ""}`} style={{ transform: `scaleX(${s.band !== null && target ? Math.min(s.band / target, 1) : 0})` }} />
                </div>
                <span className="ao-footnote ao-secondary">
                  {s.band === null
                    ? t("academyAdmin.progress.notMeasured")
                    : s.gap !== null && s.gap > 0
                      ? t("academyAdmin.ielts.progress.gap", { gap: bandText(s.gap, i18n.language) })
                      : t("academyAdmin.ielts.progress.onTarget")}
                </span>
              </li>
            );
          })}
        </ul>
        <p className="muted ao-footnote">{t("academyAdmin.ielts.progress.clbNote")}</p>
      </section>
      <section className="stack" aria-labelledby="ielts-progress-critical">
        <h3 id="ielts-progress-critical" className="ao-headline">
          {t("academyAdmin.progress.critical")}
        </h3>
        {data.critical.length ? (
          <ul className="stack">
            {data.critical.map((c, i) => (
              <li key={`${c.code}-${i}`}>{c.message}</li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t("academyAdmin.progress.noCritical")}</p>
        )}
      </section>
      <section className="stack" aria-labelledby="ielts-progress-evolution">
        <h3 id="ielts-progress-evolution" className="ao-headline">
          {t("academyAdmin.progress.evolution")}
        </h3>
        {data.evolution.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.progress.date")}</th>
                  <th>{t("academyAdmin.progress.sitting")}</th>
                  {IELTS_SKILLS.map((s) => (
                    <th key={s}>{skillName(s)}</th>
                  ))}
                  <th>{t("academyAdmin.ielts.progress.overallShort")}</th>
                </tr>
              </thead>
              <tbody>
                {data.evolution.map((row) => (
                  <tr key={row.attempt}>
                    <td className="ielts-nowrap">{dateText(row.at, i18n.language)}</td>
                    <td>{row.mock !== null ? t("academyAdmin.mocks.numberValue", { n: row.mock }) : t(`academyAdmin.progress.kind.${row.kind}`, { defaultValue: row.kind })}</td>
                    {IELTS_SKILLS.map((s) => {
                      const r = row.skills[s];
                      return (
                        <td key={s} className="ielts-nowrap ao-num">
                          {r ? `${bandText(r.band, i18n.language)}${r.clb !== null ? ` · ${t("academyAdmin.ielts.progress.clb", { clb: r.clb })}` : ""}` : "—"}
                        </td>
                      );
                    })}
                    <td className="ao-num">
                      <strong>{bandText(row.overall, i18n.language)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">{t("academyAdmin.progress.noEvolution")}</p>
        )}
      </section>
      <p className="muted">{t("academyAdmin.progress.xp", { xp: data.xp, days: data.streakDays })}</p>
    </div>
  );
}
